import { AccordionPanel } from '../../AccordionPanel';
import { formatCitation, formatPaperSectionTitle } from '../../../utils/text';
import { Paper } from '../../../models/result-set';

import { useState } from 'react';
import styled from 'styled-components';

interface PaperSubContentComponentProps {
  subContentType: 'abstract' | 'snippets' | 'citations';
  paper: Paper;
  onExpandedChange?: (isExpanded: boolean) => void;
}

export const PaperSubContentComponent = (
  props: PaperSubContentComponentProps,
): JSX.Element => {
  const { subContentType, paper, onExpandedChange = () => {} } = props;
  const [isExpanded, setIsExpanded] = useState(false);

  const handleExpandedChange = (isExpanded: boolean) => {
    setIsExpanded(isExpanded);
    onExpandedChange(isExpanded);
  };

  const { abstract, snippets = [], citationContexts = [] } = paper;

  const isEmptyAbstract =
    abstract === undefined || abstract === '' || abstract === 'unknown';
  const isEmptySnippets = snippets.length === 0;
  const isEmptyCitationContext = citationContexts.length === 0;

  if (
    (subContentType === 'abstract' && isEmptyAbstract) ||
    (subContentType === 'snippets' && isEmptySnippets) ||
    (subContentType === 'citations' && isEmptyCitationContext)
  ) {
    return <></>;
  }

  const papers = citationContexts.length > 1 ? 'Papers' : 'Paper';
  const title =
    subContentType === 'abstract'
      ? 'Abstract'
      : subContentType === 'snippets'
      ? 'Relevant Passages From Paper'
      : `Relevant Evidence From ${citationContexts.length} Citing ${papers}`;

  return (
    <AccordionPanel
      title={title}
      isExpanded={isExpanded}
      onExpandedChange={handleExpandedChange}
      className="accordion-paper-content"
    >
      <MainLayout>
        {subContentType === 'abstract' && <ContentText>{abstract}</ContentText>}
        {subContentType === 'snippets' &&
          snippets.map((snippet) => (
            <SnippetDiv key={snippet.sectionTitle}>
              <TitleSpan>
                {formatPaperSectionTitle(snippet.sectionTitle)}
              </TitleSpan>
              <ContentText>{snippet.text}</ContentText>
            </SnippetDiv>
          ))}
        {subContentType === 'citations' && (
          <CitationDiv>
            <TitleSpan>Citations:</TitleSpan>
            <ListUl>
              {citationContexts.map((citation, index) => (
                <ListLi key={`${index}-${citation.text}`}>
                  {formatCitation([citation])}
                </ListLi>
              ))}
            </ListUl>
          </CitationDiv>
        )}
      </MainLayout>
    </AccordionPanel>
  );
};

const MainLayout = styled.div`
  display: flex;
  margin: 0px 6px;
  flex-direction: column;
  gap: 10px;
`;

const CitationDiv = styled.div`
  display: flex;
  flex-direction: column;
`;

const SnippetDiv = styled.div`
  display: flex;
  flex-direction: column;
`;

const TitleSpan = styled.span`
  font-weight: 700;
  font-size: 14px;
`;

const ContentText = styled.span`
  font-size: 14px;
  font-weight: 400;
`;

const ListUl = styled.ul`
  padding-inline-start: 20px;
  margin-block-end: 0px;
  margin-block-start: 10px;
`;

const ListLi = styled.li`
  font-size: 14px;
  white-space: pre-wrap;
  font-style: italic;
`;
